// components/SurveyBuilder/LogicJumpModal.tsx
import React, { useMemo, useState } from "react";
import { Modal, Button, Select, Space, Typography, Tag } from "antd";
import {
  PlusOutlined,
  CloseOutlined,
  ArrowRightOutlined,
} from "@ant-design/icons";
import type { Answer, QuestionData } from "../../../../types/survey";

const { Text } = Typography;
const { Option } = Select;

interface LogicJumpModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (nextAnswers: Answer[]) => void;
  question: QuestionData;
  availableQuestions: Array<{ id: string; title: string }>;
}

type RuleRow = {
  answerId: string;
  answerTitle: string;
  targetFieldId?: string;
};

export const LogicJumpModal: React.FC<LogicJumpModalProps> = ({
  open,
  onClose,
  onSave,
  question,
  availableQuestions,
}) => {
  const initialRows = useMemo<RuleRow[]>(() => {
    return (question.answers || [])
      .map((a) => {
        const targetFieldId = a.logic?.[0]?.actions?.[0]?.targetFieldId;
        if (!targetFieldId) return null;
        return { answerId: a.id, answerTitle: a.title, targetFieldId };
      })
      .filter(Boolean) as RuleRow[];
  }, [question.answers]);

  const [rows, setRows] = useState<RuleRow[]>(initialRows);

  const availableAnswers = useMemo(() => {
    const used = new Set(rows.map((r) => r.answerId));
    return (question.answers || []).filter((a) => a.title && !used.has(a.id));
  }, [question.answers, rows]);

  const handleAddLogic = (answer: Answer) => {
    setRows((prev) => [
      ...prev,
      {
        answerId: answer.id,
        answerTitle: answer.title,
        targetFieldId: undefined,
      },
    ]);
  };

  const handleCancel = () => {
    setRows(initialRows);
    onClose();
  };

  const handleUpdateTarget = (index: number, targetId: string | undefined) => {
    setRows((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], targetFieldId: targetId };
      return next;
    });
  };

  const handleRemoveLogic = (index: number) => {
    setRows((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSave = () => {
    const byId = new Map(rows.map((r) => [r.answerId, r]));
    const nextAnswers: Answer[] = (question.answers || []).map((a) => {
      const row = byId.get(a.id);
      const target = row?.targetFieldId;
      if (!target) {
        return { ...a, logic: [] };
      }
      return {
        ...a,
        logic: [
          {
            when: { op: "eq", value: true },
            actions: [{ type: "skipToField", targetFieldId: target }],
          },
        ],
      };
    });
    onSave(nextAnswers);
    onClose();
  };

  const jumpTargets = [
    { id: "__next__", title: "Next question" },
    { id: "__end__", title: "End survey" },
    ...availableQuestions.filter((q) => q.id !== question.id),
  ];

  return (
    <Modal
      title="Logic jump"
      open={open}
      destroyOnHidden
      onCancel={handleCancel}
      footer={[
        <Button key="cancel" onClick={handleCancel}>
          Cancel
        </Button>,
        <Button key="save" type="primary" onClick={handleSave}>
          Done
        </Button>,
      ]}
      width={600}
    >
      <Text type="secondary" style={{ display: "block", marginBottom: 16 }}>
        Jump to different questions based on answers
      </Text>
      <div
        style={{
          background: "#f5f5f5",
          padding: "12px 16px",
          borderRadius: 6,
          marginBottom: 16,
        }}
      >
        <Text strong>Q: </Text>
        <Text>{question.title || "Untitled question"}</Text>
      </div>
      <Space orientation="vertical" style={{ width: "100%" }} size="middle">
        {rows.map((row, index) => (
          <div
            key={index}
            style={{
              borderRadius: 6,
              padding: 16,
              background: "#F5F5F5",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                width: "100%",
                marginBottom: 12,
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <Text>If answer is:</Text>
                <Tag
                  style={{
                    backgroundColor: "#0000000F",
                    fontSize: 14,
                    maxWidth: 400,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {row.answerTitle || "Untitled"}
                </Tag>
              </div>
              <Button
                type="text"
                size="small"
                icon={<CloseOutlined />}
                onClick={() => handleRemoveLogic(index)}
                style={{ marginLeft: "auto" }}
              />
            </div>

            <Space align="center">
              <ArrowRightOutlined style={{ color: "#999" }} />
              <Text>Jump to:</Text>
              <Select
                style={{ width: 250 }}
                placeholder="Select"
                value={row.targetFieldId}
                onChange={(value) => handleUpdateTarget(index, value)}
                allowClear
              >
                {jumpTargets.map((target) => (
                  <Option key={target.id} value={target.id}>
                    {target.title}
                  </Option>
                ))}
              </Select>
            </Space>
          </div>
        ))}
      </Space>
      {availableAnswers.length > 0 && (
        <div
          style={{
            marginTop: 16,
            border: "1px dashed #00000026",
            padding: "8px 12px",
            borderRadius: 6,
          }}
        >
          <Text type="secondary" style={{ display: "block", marginBottom: 8 }}>
            Add logic jump for:
          </Text>
          <Space wrap>
            {availableAnswers.map((a) => (
              <Button
                key={a.id}
                icon={<PlusOutlined />}
                onClick={() => handleAddLogic(a)}
                style={{ maxWidth: 526 }}
              >
                <span
                  style={{
                    display: "inline-block",
                    maxWidth: 520,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    verticalAlign: "middle",
                  }}
                >
                  {a.title}
                </span>
              </Button>
            ))}
          </Space>
        </div>
      )}
    </Modal>
  );
};
